import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Link } from 'react-router-dom';
import { fillUrlIds, pagePaths } from 'routes/paths';
import dayjs from 'dayjs';
import { MockLastSession, MockListProgress } from '../mockInAction';

export interface ListStatHeaderProps {
  title: string;
  totals: {
    total: number;
    learned: number;
    notLearned: number;
    outOfVocab: number;
    needsAttention: number;
    ignored: number;
  };
  progress: MockListProgress;
  lastSession?: MockLastSession;
  onOpenSource: () => void;
}

const ListStatHeader: React.FC<ListStatHeaderProps> = ({
  title,
  totals,
  progress,
  lastSession,
  onOpenSource,
}) => (
  <Card>
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={'h3'} sx={{ wordBreak: 'break-word' }}>
            {title || 'Список слів'}
          </Typography>
          <Typography color={'text.secondary'} variant={'body2'}>
            {
              'Розбито на групи: Потребують уваги · Нові · Не вивчені · Потребують повторення · Вивчені.'
            }
          </Typography>
        </Box>

        <Stack direction={'row'} spacing={1} flexShrink={0}>
          <Button
            startIcon={<ArticleOutlinedIcon />}
            variant={'text'}
            onClick={onOpenSource}
          >
            {'Переглянути джерело'}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <StatTile label={'Усього'} value={totals.total} />
        <StatTile
          label={'Вивчені'}
          value={totals.learned}
          accent={'aqua'}
          hint={pctText(totals.learned, totals.total)}
        />
        <StatTile label={'Не вивчені'} value={totals.notLearned} />
        <StatTile label={'Нові'} value={totals.outOfVocab} accent={'primary'} />
        <StatTile
          label={'Потребують уваги'}
          value={totals.needsAttention}
          accent={'warning'}
        />
        <StatTile
          label={'Ігнорується'}
          value={totals.ignored}
          accent={'muted'}
        />
      </Grid>

      <Box>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          sx={{ mb: 0.5 }}
        >
          <Typography variant={'body2'} color={'text.secondary'}>
            {`Опрацьовано: ${progress.processed} з ${progress.addressable}`}
          </Typography>
          <Typography
            variant={'body2'}
            sx={{ color: 'primary.main', fontWeight: 700 }}
          >
            {`${progress.pct}%`}
          </Typography>
        </Stack>
        <LinearProgress
          value={progress.pct}
          variant={'determinate'}
          sx={{
            bgcolor: 'primary.vocabularyUnlearned',
            borderRadius: 10,
            height: 8,
            '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
          }}
        />
      </Box>

      {lastSession && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.25}
          sx={(theme) => ({
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(155,124,255,0.10)'
                : 'rgba(59,44,147,0.05)',
            borderRadius: 2,
            px: 2,
            py: 1.25,
          })}
        >
          <Chip
            label={'Остання сесія'}
            size={'small'}
            sx={{
              bgcolor: 'primary.purple',
              color: 'primary.main',
              fontWeight: 700,
              height: 22,
            }}
          />
          <Typography variant={'body2'} sx={{ flex: 1 }}>
            {`${dayjs(lastSession.startedAt).format('DD.MM HH:mm')} · опрацьовано ${
              lastSession.itemsDone
            } з ${lastSession.itemsTotal}`}
          </Typography>
          <Button
            component={Link}
            to={fillUrlIds(pagePaths.learningSession, {
              learningSessionId: String(lastSession.sessionId),
            })}
            startIcon={<PlayArrowIcon />}
            size={'small'}
          >
            {'Продовжити'}
          </Button>
        </Stack>
      )}
    </Stack>
  </Card>
);

const pctText = (value: number, total: number) =>
  total ? `${Math.round((value / total) * 100)}%` : '';

interface StatTileProps {
  label: string;
  value: number;
  accent?: 'primary' | 'aqua' | 'warning' | 'muted';
  hint?: string;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, accent, hint }) => {
  const color =
    accent === 'aqua'
      ? 'primary.aqua'
      : accent === 'warning'
      ? '#E2A610'
      : accent === 'primary'
      ? 'primary.main'
      : accent === 'muted'
      ? 'text.secondary'
      : 'text.primary';
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <Box
        sx={(theme) => ({
          border: `1px solid ${
            theme.palette.mode === 'dark'
              ? '#303A48'
              : theme.palette.primary.tableBorder
          }`,
          borderRadius: 2,
          height: '100%',
          p: 1.5,
        })}
      >
        <Typography variant={'caption'} color={'text.secondary'}>
          {label}
        </Typography>
        <Stack direction={'row'} alignItems={'baseline'} spacing={0.75} sx={{ mt: 0.5 }}>
          <Typography variant={'h4'} sx={{ color, fontWeight: 800 }}>
            {value}
          </Typography>
          {hint && (
            <Typography variant={'caption'} color={'text.secondary'}>
              {hint}
            </Typography>
          )}
        </Stack>
      </Box>
    </Grid>
  );
};

export default ListStatHeader;
